const IMAGE_FILE_EXTENSIONS: Record<string, string> = {
  'image/apng': 'apng',
  'image/avif': 'avif',
  'image/bmp': 'bmp',
  'image/gif': 'gif',
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/svg+xml': 'svg',
  'image/tiff': 'tiff',
  'image/vnd.microsoft.icon': 'ico',
  'image/webp': 'webp',
};

export interface SerializedClipboardFile {
  name: string;
  lastModified: number;
  data: ArrayBuffer;
}

export interface ReadClipboardFilesResult {
  files: SerializedClipboardFile[];
  rejectedCount: number;
}

interface AttachmentPasteOptions {
  event: ClipboardEvent;
  uploadFiles: (files: File[]) => void;
  readFilesFromClipboard?: () => Promise<ReadClipboardFilesResult>;
  onPathPasteUnsupported?: () => void;
  onFilesRejected?: (rejectedCount: number, addedCount: number) => void;
  onPathReadError?: (error: unknown) => void;
}

export function listenForAttachmentPaste(
  enabled: boolean,
  listener: (event: ClipboardEvent) => void
): () => void {
  if (!enabled) return () => {};

  window.addEventListener('paste', listener, true);
  return () => {
    window.removeEventListener('paste', listener, true);
  };
}

function stripMatchingQuotes(value: string) {
  const first = value.at(0);
  const last = value.at(-1);
  return value.length >= 2 && first === last && (first === '"' || first === "'")
    ? value.slice(1, -1)
    : value;
}

function ensureClipboardFileName(file: File): File {
  if (file.name.trim()) return file;

  const extension = IMAGE_FILE_EXTENSIONS[file.type];
  const name = extension ? `pasted-image.${extension}` : 'pasted-file';
  return new File([file], name, {
    type: file.type,
    lastModified: file.lastModified,
  });
}

export function getFilesFromClipboard(
  clipboardData: DataTransfer | null
): File[] {
  if (!clipboardData) return [];

  const files = Array.from(clipboardData.files ?? []);
  if (files.length > 0) {
    return files.map(ensureClipboardFileName);
  }

  return Array.from(clipboardData.items ?? [])
    .filter(item => item.kind === 'file')
    .map(item => item.getAsFile())
    .filter((file): file is File => file !== null)
    .map(ensureClipboardFileName);
}

export function isClipboardFilePathText(text: string): boolean {
  const lines = text
    .split(/\r?\n/)
    .map(line => stripMatchingQuotes(line.trim()))
    .filter(Boolean);

  return (
    lines.length > 0 &&
    lines.every(
      line =>
        line.startsWith('/') ||
        /^file:\/\//i.test(line) ||
        /^[a-z]:[\\/]/i.test(line) ||
        line.startsWith('\\\\')
    )
  );
}

export function deserializeClipboardFile(file: SerializedClipboardFile): File {
  return new File([file.data], file.name, {
    lastModified: file.lastModified,
  });
}

function consumePaste(event: ClipboardEvent) {
  event.preventDefault();
  event.stopPropagation();
}

export function handleAttachmentPaste({
  event,
  uploadFiles,
  readFilesFromClipboard,
  onPathPasteUnsupported,
  onFilesRejected,
  onPathReadError,
}: AttachmentPasteOptions): boolean {
  const files = getFilesFromClipboard(event.clipboardData);
  if (files.length > 0) {
    consumePaste(event);
    uploadFiles(files);
    return true;
  }

  const text = event.clipboardData?.getData('text/plain') ?? '';
  if (!isClipboardFilePathText(text)) {
    return false;
  }

  consumePaste(event);
  if (!readFilesFromClipboard) {
    onPathPasteUnsupported?.();
    return true;
  }

  Promise.resolve()
    .then(readFilesFromClipboard)
    .then(result => {
      const clipboardFiles = result.files.map(deserializeClipboardFile);
      if (clipboardFiles.length > 0) {
        uploadFiles(clipboardFiles);
      }
      if (result.rejectedCount > 0 || clipboardFiles.length === 0) {
        onFilesRejected?.(result.rejectedCount || 1, clipboardFiles.length);
      }
    })
    .catch(error => {
      onPathReadError?.(error);
    });

  return true;
}
